'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import ThemeSwitcher from '@/components/theme/ThemeSwitcher';
import { tools, navLinks } from '@/config/tools';

// Featured tools for direct header links (top 4 by search volume)
const featuredTools = tools.filter((tool) =>
  ['/merge-pdf', '/split-pdf', '/compress-pdf', '/rotate-pdf'].includes(tool.href)
);
const moreTools = tools.filter((tool) => !featuredTools.includes(tool));

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);

  const pathname = usePathname();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const firstMobileItemRef = useRef<HTMLAnchorElement>(null);

  // ── Scroll handler ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Close mobile menu on route change ───────────────────────────────────────
  useEffect(() => {
    setMobileOpen(false);
    setMobileToolsOpen(false);
    setToolsDropdownOpen(false);
  }, [pathname]);

  // ── Body scroll lock when mobile menu is open ────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // ── Focus first item when mobile menu opens ─────────────────────────────────
  useEffect(() => {
    if (mobileOpen) {
      // Small timeout to let the DOM render before shifting focus
      const t = setTimeout(() => firstMobileItemRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [mobileOpen]);

  // ── Global keyboard handler ──────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (toolsDropdownOpen) {
          setToolsDropdownOpen(false);
        }
        if (mobileOpen) {
          setMobileOpen(false);
          hamburgerRef.current?.focus();
        }
      }
    },
    [toolsDropdownOpen, mobileOpen]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // ── Focus trap for mobile menu ───────────────────────────────────────────────
  // Keeps Tab / Shift+Tab cycling inside the open dialog
  useEffect(() => {
    if (!mobileOpen || !mobileMenuRef.current) return;

    const focusable = mobileMenuRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [mobileOpen, mobileToolsOpen]); // re-run when sub-list expands (new focusable items)

  // ── Close dropdown when clicking outside ────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setToolsDropdownOpen(false);
      }
    };
    if (toolsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [toolsDropdownOpen]);

  return (
    <>
      {/* ── Skip to main content — critical for screen readers & keyboard nav ── */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-lg focus:font-heading focus:font-bold focus:text-sm focus:shadow-lg"
      >
        Skip to main content
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(26,26,46,0.08)]'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
        role="banner"
      >
        <nav
          className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
            aria-label="LoveUPDF — Free Privacy-First PDF Tools — Go to homepage"
          >
            <Image
              src="/assets/images/app_logo.png"
              alt=""
              width={48}
              height={48}
              className="w-10 h-10 object-contain"
              priority
            />
            <span className="font-heading tracking-tight text-brand-dark font-black text-[22px] leading-none">
              LoveU<span className="text-primary">PDF</span>
            </span>
          </Link>

          {/* ── Desktop nav ───────────────────────────────────────────────────── */}
          <div className="hidden md:flex items-center gap-0.5" role="list">
            {/* Direct tool links */}
            {featuredTools.map((tool) => {
              const isActive = pathname === tool.href;
              return (
                <Link
                  key={tool.href}
                  href={tool.href}
                  role="listitem"
                  className={`px-3 lg:px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 font-body whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-light text-primary font-semibold'
                      : 'text-brand-mid hover:text-brand-dark hover:bg-brand-surface'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {tool.title}
                </Link>
              );
            })}

            {/* ── More Tools dropdown ─────────────────────────────────────────── */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setToolsDropdownOpen(true)}
              onMouseLeave={() => setToolsDropdownOpen(false)}
            >
              <button
                onClick={() => setToolsDropdownOpen((prev) => !prev)}
                onFocus={() => setToolsDropdownOpen(true)}
                className="flex items-center gap-1 px-3 lg:px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 text-brand-mid hover:text-brand-dark hover:bg-brand-surface font-body"
                aria-expanded={toolsDropdownOpen}
                aria-haspopup="menu"
                aria-controls="tools-dropdown-menu"
              >
                More Tools
                <Icon
                  name="ChevronDownIcon"
                  size={14}
                  className={`transition-transform duration-200 ${toolsDropdownOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {/* Dropdown menu */}
              <div
                id="tools-dropdown-menu"
                className={`absolute top-full left-1/2 -translate-x-1/2 w-[272px] pt-2 transition-all duration-200 ${
                  toolsDropdownOpen
                    ? 'opacity-100 visible translate-y-0 pointer-events-auto'
                    : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                }`}
                role="menu"
                aria-label="More PDF tools"
              >
                <div className="bg-white rounded-2xl shadow-[0_10px_50px_rgba(26,26,46,0.15)] border border-brand-border overflow-hidden p-2">
                  {moreTools.map((tool) => {
                    const isActive = pathname === tool.href;
                    return (
                      <Link
                        key={tool.href}
                        href={tool.href}
                        className={`group/item flex items-center gap-3 px-4 py-3 rounded-xl text-[13.5px] font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary font-body ${
                          isActive
                            ? 'text-primary bg-primary-light'
                            : 'text-brand-mid hover:text-primary hover:bg-primary-light'
                        }`}
                        role="menuitem"
                        aria-current={isActive ? 'page' : undefined}
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Tab' &&
                            !e.shiftKey &&
                            tool === moreTools[moreTools.length - 1]
                          ) {
                            setToolsDropdownOpen(false);
                          }
                        }}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover/item:scale-110"
                          style={{ background: `${tool.color}15` }}
                          aria-hidden="true"
                        >
                          <Icon name={tool.icon as any} size={17} style={{ color: tool.color }} />
                        </div>
                        <span>{tool.title}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Other nav links (About, All Tools, etc.) */}
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  role="listitem"
                  className={`px-3 lg:px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 font-body whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-light text-primary font-semibold'
                      : 'text-brand-mid hover:text-brand-dark hover:bg-brand-surface'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {link?.label}
                </Link>
              );
            })}
          </div>

          {/* ── Mobile hamburger ────────────────────────────────────────────────── */}
          <div className="flex items-center gap-2">
            <ThemeSwitcher className="hidden sm:inline-flex" />
            <button
              ref={hamburgerRef}
              onClick={() => setMobileOpen((prev) => !prev)}
              className="md:hidden p-2 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 text-brand-dark hover:bg-brand-surface"
              aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} aria-hidden="true" />
            </button>
          </div>
        </nav>
      </header>

      {/* ── Mobile menu ─────────────────────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 md:hidden"
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        ref={mobileMenuRef}
        id="mobile-menu"
        className={`fixed inset-x-0 top-16 bottom-0 z-40 flex flex-col bg-white transition-all duration-300 md:hidden ${
          mobileOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        aria-hidden={!mobileOpen}
      >
        <nav
          className="flex flex-col px-4 py-5 gap-1.5 overflow-y-auto flex-1"
          aria-label="Mobile navigation"
        >
          {/* Popular tools section */}
          <div className="flex items-center justify-between gap-3 px-4 pt-1 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-muted">
              Popular Tools
            </p>
            <ThemeSwitcher className="sm:hidden" />
          </div>
          {featuredTools.map((tool, idx) => {
            const isActive = pathname === tool.href;
            return (
              <Link
                key={tool.href}
                href={tool.href}
                ref={idx === 0 ? firstMobileItemRef : undefined}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-[15px] font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary font-body ${
                  isActive
                    ? 'text-primary bg-primary-light'
                    : 'text-brand-dark hover:bg-brand-surface active:bg-brand-surface'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${tool.color}15` }}
                  aria-hidden="true"
                >
                  <Icon name={tool.icon as any} size={17} style={{ color: tool.color }} />
                </div>
                <span>{tool.title}</span>
              </Link>
            );
          })}

          {/* More tools collapsible */}
          <div className="mt-1">
            <button
              onClick={() => setMobileToolsOpen((prev) => !prev)}
              className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-[15px] font-medium transition-all hover:bg-brand-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-brand-dark font-body"
              aria-expanded={mobileToolsOpen}
              aria-controls="mobile-tools-menu"
            >
              <span>More Tools</span>
              <Icon
                name="ChevronDownIcon"
                size={18}
                className={`transition-transform duration-200 ${mobileToolsOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            <div
              id="mobile-tools-menu"
              className={`overflow-hidden transition-all duration-200 ${
                mobileToolsOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
              role="region"
              aria-label="More PDF tools"
            >
              <div className="flex flex-col gap-0.5 pl-4 pt-1 border-l-2 border-brand-border ml-6 mb-1">
                {moreTools.map((tool) => {
                  const isActive = pathname === tool.href;
                  return (
                    <Link
                      key={tool.href}
                      href={tool.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-[14px] font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary font-body ${
                        isActive
                          ? 'text-primary bg-primary-light'
                          : 'text-brand-mid hover:bg-brand-surface active:bg-brand-surface'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${tool.color}15` }}
                        aria-hidden="true"
                      >
                        <Icon name={tool.icon as any} size={15} style={{ color: tool.color }} />
                      </div>
                      <span>{tool.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Divider + other nav links (About, etc.) */}
          <div className="mt-2 pt-3 border-t border-brand-border flex flex-col gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-[15px] font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary font-body ${
                    isActive
                      ? 'text-primary bg-primary-light'
                      : 'text-brand-dark hover:bg-brand-surface active:bg-brand-surface'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {link?.label}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Privacy badge pinned to bottom */}
        <div className="px-6 py-4 border-t border-brand-border bg-white">
          <p className="text-xs text-center text-brand-muted leading-relaxed">
            <span className="font-medium text-brand-mid">🔒 Privacy first:</span> No storage, no
            account, 100% free
          </p>
        </div>
      </div>
    </>
  );
}
